import React from 'react';
import { Search, Filter, X } from 'lucide-react';

interface UserFiltersProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  selectedNationality: string;
  onNationalityChange: (nationality: string) => void;
  selectedGender: string;
  onGenderChange: (gender: string) => void;
  ageRange: { min: number; max: number };
  onAgeRangeChange: (range: { min: number; max: number }) => void;
  nationalityOptions: string[];
  genderOptions: string[];
}

const UserFilters: React.FC<UserFiltersProps> = ({
  searchTerm,
  onSearchChange,
  selectedNationality,
  onNationalityChange,
  selectedGender,
  onGenderChange,
  ageRange,
  onAgeRangeChange,
  nationalityOptions,
  genderOptions
}) => {
  const clearFilters = () => {
    onSearchChange('');
    onNationalityChange('all');
    onGenderChange('all');
    onAgeRangeChange({ min: 0, max: 100 });
  };

  const hasActiveFilters = searchTerm || selectedNationality !== 'all' || selectedGender !== 'all' || 
                           ageRange.min !== 0 || ageRange.max !== 100;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Filter className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Filter Users</h3>
        </div>
        
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
          >
            <X className="h-4 w-4 mr-1" />
            Clear Filters
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Search Users</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {/* Nationality Filter */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Nationality</label>
          <select
            value={selectedNationality}
            onChange={(e) => onNationalityChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          >
            <option value="all">All Nationalities</option>
            {nationalityOptions.map((nationality) => (
              <option key={nationality} value={nationality}>
                {nationality}
              </option>
            ))}
          </select>
        </div>

        {/* Gender Filter */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Gender</label>
          <select
            value={selectedGender}
            onChange={(e) => onGenderChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          >
            <option value="all">All Genders</option>
            {genderOptions.map((gender) => (
              <option key={gender} value={gender.toLowerCase()}>
                {gender}
              </option>
            ))}
          </select>
        </div>

        {/* Age Range Filter */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Age Range</label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              placeholder="Min"
              value={ageRange.min}
              onChange={(e) => onAgeRangeChange({ ...ageRange, min: Number(e.target.value) })}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
              min="0"
              max="100"
            />
            <span className="text-gray-500">-</span>
            <input
              type="number"
              placeholder="Max"
              value={ageRange.max}
              onChange={(e) => onAgeRangeChange({ ...ageRange, max: Number(e.target.value) })}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
              min="0"
              max="100"
            />
          </div>
          <div className="text-xs text-gray-500">
            Current: {ageRange.min} - {ageRange.max} years
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {searchTerm && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                Search: "{searchTerm}"
                <button
                  onClick={() => onSearchChange('')}
                  className="ml-2 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            {selectedNationality !== 'all' && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                Nationality: {selectedNationality}
                <button
                  onClick={() => onNationalityChange('all')}
                  className="ml-2 hover:text-green-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            {selectedGender !== 'all' && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                Gender: {selectedGender}
                <button
                  onClick={() => onGenderChange('all')}
                  className="ml-2 hover:text-purple-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            {(ageRange.min !== 0 || ageRange.max !== 100) && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-800">
                Age: {ageRange.min}-{ageRange.max}
                <button
                  onClick={() => onAgeRangeChange({ min: 0, max: 100 })}
                  className="ml-2 hover:text-orange-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFilters;